/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import apiService from "services/apiService";
import DocumentForm, { AddDocPayload } from "./DocumentForm";
import Modal from "components/Modal";
import { useToaster } from "contexts/toasterContext";
import {
  FiTrash2,
  FiCheck,
  FiX,
  // FiSearch,
  FiChevronUp,
  FiChevronDown,
  // FiPlus,
} from "react-icons/fi";
import HeaderButtons from "components/Button/HeaderButtons";
import { FaTrash, FaEdit, FaAngleLeft, FaAngleRight } from "react-icons/fa";
import { TruncatedText } from "pages/AdminConfig/components/TruncatedText";
import Tooltip from "components/Tooltip";

interface Doc {
  id: number;
  name: string;
  optional: boolean; // false = required, true = optional
  tempDeleted: boolean;
}

const NAME_PATTERN = /^[a-zA-Z0-9@#$%&()/-_, .]+$/;
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

const validateName = (value: string): string => {
  // Trim before all checks so whitespace-only ("   ") is treated as empty
  if (!value.trim()) return "Document name cannot be blank";
  if (!NAME_PATTERN.test(value.trim())) return "Invalid characters in document name";
  return "";
};

// ── Shared micro-components ──────────────────────────────────────────────────

// Accessible toggle switch for inline editable rows
function ToggleSwitch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${checked ? "bg-blue-600" : "bg-gray-200 dark:bg-gray-600"
        } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"
          }`}
      />
    </button>
  );
}

type BadgeColor = "green" | "gray" | "blue" | "red" | "yellow";

// Lightweight status / label badge
function Badge({ label, color }: { label: string; color: BadgeColor }) {
  const palette: Record<BadgeColor, string> = {
    green:
      "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800/50",
    gray: "bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600/50",
    blue: "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/50",
    red: "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/50",
    yellow:
      "bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800/50",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${palette[color]}`}
    >
      {label}
    </span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const DocumentList: React.FC = () => {
  // ── Data state ──
  const [documents, setDocuments] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // ── Inline edit state ──
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editRequired, setEditRequired] = useState(false); // required = !optional
  const [editNameError, setEditNameError] = useState("");
  const [savingId, setSavingId] = useState<number | null>(null);

  // ── Delete state ──
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // ── Table controls ──
  const [searchTerm, setSearchTerm] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filterRequired, setFilterRequired] = useState<"all" | "yes" | "no">("all");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "deleted">("all");
  const [sortField, setSortField] = useState<"name" | "id">("name"); // default: alphabetical A→Z
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { addToast } = useToaster();

  // ── API helpers ──────────────────────────────────────────────────────────

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const res = await apiService.get("api/documentNames/list");
      setDocuments(res.data);
    } catch (err) {
      console.error("Failed to fetch documents:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Add document — called from the add-document modal
  const handleAdd = async (payload: AddDocPayload) => {
    setAdding(true);
    try {
      await apiService.post("api/documentNames/add", [payload]);
      addToast("Document added successfully", "success");
      setIsAddModalOpen(false); // close modal on success
      fetchDocuments();
    } catch (err) {
      console.error("Failed to add document:", err);
      addToast("Failed to add document", "error");
    } finally {
      setAdding(false);
    }
  };

  // ── Inline edit handlers ─────────────────────────────────────────────────

  const startEdit = (doc: Doc) => {
    setEditingId(doc.id);
    setEditName(doc.name);
    setEditRequired(!doc.optional); // invert: optional=false means required=true
    setEditNameError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditRequired(false);
    setEditNameError("");
  };

  const saveEdit = async (id: number) => {
    const err = validateName(editName);
    if (err) { setEditNameError(err); return; }
    setSavingId(id);
    try {
      await apiService.put("api/documentNames/update", [
        { id, name: editName.trim(), optional: !editRequired },
      ]);
      addToast("Document updated successfully", "success");
      cancelEdit();
      fetchDocuments();
    } catch (err) {
      console.error("Failed to update document:", err);
      addToast("Failed to update document", "error");
    } finally {
      setSavingId(null);
    }
  };

  // ── Delete handlers ──────────────────────────────────────────────────────

  const handleDeleteClick = (id: number) => {
    setDeleteTargetId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    const id = deleteTargetId;
    setDeletingId(id);
    setIsDeleteModalOpen(false);
    setDeleteTargetId(null);
    try {
      await apiService.delete(`api/documentNames/${id}`);
      addToast("Document deleted successfully", "success");
      fetchDocuments();
    } catch (err) {
      console.error("Failed to delete document:", err);
      addToast("Failed to delete document", "error");
    } finally {
      setDeletingId(null);
    }
  };

  // ── Sort toggle ──────────────────────────────────────────────────────────

  const handleSort = (field: "name" | "id") => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setPage(1);
  };

  // ── Derived data: filter → sort → paginate ───────────────────────────────

  const processed = useMemo(() => {
    let list = [...documents];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter((d) => d.name.toLowerCase().includes(term));
    }
    if (filterRequired === "yes") list = list.filter((d) => !d.optional);
    if (filterRequired === "no") list = list.filter((d) => d.optional);
    if (filterStatus === "active") list = list.filter((d) => !d.tempDeleted);
    if (filterStatus === "deleted") list = list.filter((d) => d.tempDeleted);

    list.sort((a, b) => {
      const aVal = sortField === "name" ? a.name.toLowerCase() : a.id;
      const bVal = sortField === "name" ? b.name.toLowerCase() : b.id;
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [documents, searchTerm, filterRequired, filterStatus, sortField, sortDir]);

  // Reset to page 1 whenever filters or page size change
  useEffect(() => { setPage(1); }, [searchTerm, filterRequired, filterStatus, sortField, sortDir, pageSize]);

  const totalPages = Math.max(1, Math.ceil(processed.length / pageSize));
  const paginated = processed.slice((page - 1) * pageSize, page * pageSize);

  // Clamp page if filters or page size reduce the total available pages
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  // ── Sort icon renderer ───────────────────────────────────────────────────

  const renderSortIcon = (field: "name" | "id") => (
    <span className="inline-flex flex-col ml-0.5 opacity-50">
      <FiChevronUp
        size={10}
        className={sortField === field && sortDir === "asc" ? "opacity-100 text-blue-500" : ""}
      />
      <FiChevronDown
        size={10}
        style={{ marginTop: -2 }}
        className={sortField === field && sortDir === "desc" ? "opacity-100 text-blue-500" : ""}
      />
    </span>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="text-slate-900 dark:text-white bg-white dark:bg-gray-800 transition-colors duration-300 overflow-hidden h-full flex flex-col">
      <div className="space-y-2 px-1 pb-2 flex-1 min-h-0 flex flex-col">

        {/* Table toolbar — search + filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Search input */}
          <div className="relative w-48 sm:w-56 lg:w-72 xl:w-96 2xl:w-[28rem]">
            {/* <FiSearch
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={14}
            /> */}
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
          </div>



          {/* Result count */}
          <span className="text-xs text-gray-400 dark:text-gray-500 sm:ml-auto shrink-0 invisible">
            {processed.length} {processed.length === 1 ? "result" : "results"}
          </span>
          <HeaderButtons
            variant="primary"
            // icon={<FiPlus size={15} />}
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Document
          </HeaderButtons>
        </div>

        {/* Document table — fixed viewport height: header sticky, body scrolls */}
        <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 flex-1 min-h-0">
          {/* Scrollable area — contains both thead (sticky) and tbody */}
          <div className="flex-1 overflow-y-auto overflow-x-auto min-h-0">
            <table className="min-w-full text-sm">

              {/* Sticky header — stays visible while body scrolls */}
              <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  {/* Sortable: Document Name */}
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400  tracking-wider cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                    onClick={() => handleSort("name")}
                  >
                    <span className="inline-flex items-center gap-0.5">
                      Document Name
                      {renderSortIcon("name")}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400  tracking-wider">
                    Required
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400  tracking-wider">
                    Soft Delete
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400  tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400  tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700/60">

                {/* Skeleton rows — match pageSize so table height stays stable during loading */}
                {loading && Array.from({ length: pageSize }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="animate-pulse border-b border-gray-100 dark:border-gray-700/60">
                    <td className="px-4 py-3"><div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" /></td>
                    <td className="px-4 py-3"><div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-md w-16" /></td>
                    <td className="px-4 py-3"><div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-md w-10" /></td>
                    <td className="px-4 py-3"><div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-md w-14" /></td>
                    <td className="px-4 py-3 text-right"><div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-md w-14 ml-auto" /></td>
                  </tr>
                ))}

                {/* Empty state */}
                {!loading && paginated.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500">
                        <svg
                          className="w-10 h-10 opacity-40"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <p className="text-sm font-medium">No documents found</p>
                        {(searchTerm || filterRequired !== "all" || filterStatus !== "all") && (
                          <p className="text-xs">Try adjusting your search or filters</p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {/* Data rows */}
                {!loading &&
                  paginated.map((doc) => {
                    const isEditing = editingId === doc.id;
                    const isSaving = savingId === doc.id;
                    const isDeleting = deletingId === doc.id;

                    return (
                      <tr
                        key={doc.id}
                        className={`transition-colors duration-150 ${isEditing
                          ? "bg-blue-50/60 dark:bg-blue-900/10"
                          : "hover:bg-gray-50 dark:hover:bg-gray-700/30"
                          } ${isDeleting ? "opacity-50 pointer-events-none" : ""}`}
                      >
                        {/* Document Name — becomes input in edit mode */}
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                          {isEditing ? (
                            <div className="min-w-[180px] max-w-xs">
                              <input
                                type="text"
                                value={editName}
                                autoFocus
                                disabled={isSaving}
                                onChange={(e) => {
                                  // Auto-capitalize first character
                                  const raw = e.target.value;
                                  const val = raw.charAt(0).toUpperCase() + raw.slice(1);
                                  setEditName(val);
                                  // Always re-validate so whitespace-only entry shows error immediately
                                  setEditNameError(validateName(val));
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveEdit(doc.id);
                                  if (e.key === "Escape") cancelEdit();
                                }}
                                className={`w-full px-2.5 py-1.5 text-sm rounded-lg border ${editNameError
                                  ? "border-red-400 focus:ring-red-400"
                                  : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                                  } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:border-transparent transition`}
                              />
                              {editNameError && (
                                <p className="mt-0.5 text-xs text-red-500">{editNameError}</p>
                              )}
                            </div>
                          ) : (
                            <TruncatedText
                              text={doc.name}
                              className="w-[150px] sm:w-[200px] lg:w-[250px] font-medium text-gray-900 dark:text-gray-100"
                            />
                          )}
                        </td>

                        {/* Required — badge in view, toggle in edit */}
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <ToggleSwitch
                              checked={editRequired}
                              onChange={setEditRequired}
                              disabled={isSaving}
                            />
                          ) : (
                            <Badge
                              label={!doc.optional ? "Required" : "Optional"}
                              color={!doc.optional ? "blue" : "gray"}
                            />
                          )}
                        </td>

                        {/* Soft Delete — read-only badge */}
                        <td className="px-4 py-3">
                          <Badge
                            label={doc.tempDeleted ? "Yes" : "No"}
                            color={doc.tempDeleted ? "yellow" : "gray"}
                          />
                        </td>

                        {/* Status — derived from tempDeleted */}
                        <td className="px-4 py-3">
                          <Badge
                            label={doc.tempDeleted ? "Deleted" : "Active"}
                            color={doc.tempDeleted ? "red" : "green"}
                          />
                        </td>

                        {/* Actions — Save/Cancel in edit, Edit/Delete in view */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {isEditing ? (
                              <>
                                {/* Save inline edit */}
                                <button
                                  onClick={() => saveEdit(doc.id)}
                                  disabled={isSaving}
                                  title="Save changes (Enter)"
                                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-900/50 text-white transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                >
                                  {isSaving ? (
                                    <div className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                  ) : (
                                    <FiCheck size={13} />
                                  )}
                                  Save
                                </button>

                                {/* Cancel inline edit */}
                                <button
                                  onClick={cancelEdit}
                                  disabled={isSaving}
                                  title="Cancel edit (Escape)"
                                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 disabled:opacity-50 transition focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
                                >
                                  <FiX size={13} />
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                {/* Edit — disabled when another row is being edited */}
                                <button
                                  onClick={() => startEdit(doc)}
                                  disabled={editingId !== null || isDeleting}
                                  title="Edit document"
                                  className="btn-primary px-2 py-2 rounded-lg"
                                >
                                  <FaEdit size={14} />
                                </button>

                                {/* Delete */}
                                <button
                                  onClick={() => handleDeleteClick(doc.id)}
                                  disabled={editingId !== null || isDeleting}
                                  title="Delete document"
                                  className="btn-red px-2 py-2 rounded-lg"
                                >
                                  {isDeleting ? (
                                    <div className="h-3.5 w-3.5 rounded-full border-2 border-red-400 border-t-transparent animate-spin" />
                                  ) : (
                                    <FaTrash size={14} />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>{/* end scrollable area */}

          {/* Pagination footer — always rendered to prevent layout shift */}
          <div className="flex-shrink-0 items-center w-full pt-2 pb-2 py-2 pl-4 pr-6 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 sm:flex sm:justify-between">
            {/* Left: result count + items-per-page selector */}
            <div className="flex items-center mb-0 sm:mb-0">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                disabled={loading}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>

              <span className="counting text-base font-normal text-gray-500 dark:text-[#E3E3E3] ml-2">
                {loading ? (
                  "Loading…"
                ) : processed.length === 0 ? (
                  "No results"
                ) : (
                  <>
                    Showing{" "}
                    <span className="counting dark:text-[#E3E3E3]">
                      {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, processed.length)}
                    </span>{" "}
                    out of{" "}
                    <span className="counting dark:text-[#E3E3E3]">
                      {processed.length}
                    </span>
                  </>
                )}
              </span>
            </div>

            {/* Right: page navigation */}
            <div className="flex items-center space-x-3">
              {!loading && page !== 1 && (
                <Tooltip content="Click to go Previous">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="action-btn btn-primary px-3 py-2 mr-1 rounded-lg inline-flex items-center"
                  >
                    <FaAngleLeft className="mr-1" />
                    Previous
                  </button>
                </Tooltip>
              )}

              {!loading && page * pageSize < processed.length && (
                <Tooltip content="Click to go Next">
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="action-btn btn-primary px-3 py-2 rounded-lg inline-flex items-center"
                  >
                    Next
                    <FaAngleRight className="ml-1" />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>{/* end table card */}
      </div>

      {/* Add Document modal */}
      {isAddModalOpen && (
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          header="Add Document"
          size="sm"
        >
          <DocumentForm
            onAdd={handleAdd}
            adding={adding}
            onClose={() => setIsAddModalOpen(false)}
          />
        </Modal>
      )}

      {/* Delete confirmation modal */}
      {isDeleteModalOpen && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setDeleteTargetId(null);
          }}
          header="Delete Document"
          size="sm"
        >
          <div className="p-5 text-center">
            <div className="mx-auto mb-4 flex items-center justify-center w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/30">
              <FiTrash2 className="text-red-500 dark:text-red-400" size={22} />
            </div>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
              Are you sure you want to delete{" "}
              <span className="font-bold text-gray-900 dark:text-white">
                {documents.find((d) => d.id === deleteTargetId)?.name}
              </span>
              ?
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">
              This action cannot be undone.
            </p>
            <div className="flex items-center justify-center gap-12">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeleteTargetId(null);
                }}
                className="whitespace-nowrap px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="whitespace-nowrap px-4 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              >
                Yes, delete
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DocumentList;
